import { PaginatedResponse } from '@models/api/data.types';
import { LanguageKey } from '@models/Language';
import {
  ConfirmParkingAgreementResponse,
  ConfirmParkingChangeAgreementRequest,
  CreateParkingAgreementDraftRequest,
  CreateParkingAgreementRequest,
  CreateParkingChangeAgreementRequest,
  CreateParkingWatchRequest,
  CreateParkingWatchResponse,
  ParkingAgreementDraftResponse,
  ParkingLockAPIResponse,
  ParkingProduct,
  ParkingWatch,
  TerminateParkingContractRequest,
  TerminateParkingContractResponse,
  UpdateParkingAgreementDraftRequest,
  UpdateParkingChangeAgreementRequest,
} from '@models/parking.types';
import { createAsyncTask } from '@stores/taskStore';

import { AGREEMENTS_API_BASE_URL } from './api.constants';
import { authClient } from './client.api';

export const REAL_ESTATE_V1_API_URL = process.env.REAL_ESTATE_API_URL ?? '';
export const REAL_ESTATE_V2_API_URL = process.env.REAL_ESTATE_V2_API_URL ?? '';

export const parkingApi = {
  /**
   *  Fetch parking space products for a real estate
   */
  getRealEstateParkingProducts: (realEstateId: string, language: LanguageKey) =>
    authClient.get<PaginatedResponse<ParkingProduct>>(
      `${REAL_ESTATE_V2_API_URL}/real-estate/${realEstateId}/parkingspaceproducts`,
      {
        params: { lang: language },
      }
    ),

  /**
   * Fetch a parking space product by its ID.
   */
  getParkingProduct: (parkingSpaceId: string, language: LanguageKey) =>
    authClient.get<ParkingProduct>(
      `${REAL_ESTATE_V2_API_URL}/product/parking/${parkingSpaceId}`,
      { params: { lang: language } }
    ),

  /** Locks a parking product for the user to rent */
  lockParkingProduct: createAsyncTask(
    'lock-parking-product',
    (parkingProductId: string) =>
      authClient.post<ParkingLockAPIResponse>(
        `${REAL_ESTATE_V1_API_URL}/quickrent/parking/lock`,
        { productId: parkingProductId }
      )
  ),

  /** Unlocks a locked parking product. Use `cancelParkingProductRenting` if draft parking agreement has been created (i.e. if user has opened the draft contract PDF). */
  unlockParkingProduct: createAsyncTask(
    'unlock-parking-product',
    (parkingProductId: string) =>
      authClient.post(`${REAL_ESTATE_V1_API_URL}/quickrent/parking/unlock`, {
        productId: parkingProductId,
      })
  ),

  /** Cancels renting process of a locked parking product. */
  cancelParkingProductRenting: createAsyncTask(
    'cancel-parking-product',
    (draftAgreementId: string) =>
      authClient.post(`${AGREEMENTS_API_BASE_URL}/quickrent/parking/cancel`, {
        offerId: draftAgreementId,
      })
  ),

  /**
   * Creates a draft parking agreement to CMS.
   *
   * NOTE: This has to have been called at least once before parking agreement can be submitted.
   *
   * @returns an object containing draft agreement id, and PDF as base64 string.
   */
  createAndFetchParkingAgreementDraft: createAsyncTask(
    'create-and-fetch-parking-agreement-draft',
    (requestData: CreateParkingAgreementDraftRequest) =>
      authClient.post<ParkingAgreementDraftResponse>(
        `${AGREEMENTS_API_BASE_URL}/quickrent/parking/create`,
        requestData
      )
  ),

  /**
   * Updates a draft parking agreement that was created using `createAndFetchParkingAgreementDraft`.
   *
   * @returns an object containing draft agreement id, and PDF as base64 string.
   * */
  updateAndFetchParkingAgreementDraft: createAsyncTask(
    'update-and-fetch-parking-agreement-draft',
    (requestData: UpdateParkingAgreementDraftRequest) =>
      authClient.post<ParkingAgreementDraftResponse>(
        `${AGREEMENTS_API_BASE_URL}/quickrent/parking/update`,
        requestData
      )
  ),

  createParkingAgreement: createAsyncTask(
    'create-parking-agreement',
    (requestData: CreateParkingAgreementRequest) =>
      authClient.post<ConfirmParkingAgreementResponse>(
        `${AGREEMENTS_API_BASE_URL}/quickrent/parking/confirm`,
        requestData
      )
  ),

  /**
   * Creates a parking change draft agreement and returns an offer with draft PDF.
   * POST quickrent/parking/change/create
   */
  createParkingChangeAgreementDraft: createAsyncTask(
    'create-parking-change-agreement',
    (requestData: CreateParkingChangeAgreementRequest) =>
      authClient.post(
        `${AGREEMENTS_API_BASE_URL}/quickrent/parking/change/create`,
        requestData
      )
  ),

  /**
   * Updates a parking change draft agreement and returns an offer with draft PDF.
   * POST quickrent/parking/change/update
   */
  updateParkingChangeAgreementDraft: createAsyncTask(
    'update-parking-change-agreement',
    (requestData: UpdateParkingChangeAgreementRequest) =>
      authClient.post(
        `${AGREEMENTS_API_BASE_URL}/quickrent/parking/change/update`,
        requestData
      )
  ),

  /**
   * Creates a new parking contract and terminates the old one.
   * POST quickrent/parking/change/confirm
   */
  confirmParkingChangeAgreement: createAsyncTask(
    'confirm-parking-change-agreement',
    (requestData: ConfirmParkingChangeAgreementRequest) =>
      authClient.post<ConfirmParkingAgreementResponse>(
        `${AGREEMENTS_API_BASE_URL}/quickrent/parking/change/confirm`,
        requestData
      )
  ),

  /**
   * Creates a new parking space watch.
   * POST search/parking/watch
   */
  createParkingWatch: (payload: CreateParkingWatchRequest) =>
    authClient.post<CreateParkingWatchResponse>(
      `${REAL_ESTATE_V1_API_URL}/search/parking/watch`,
      payload
    ),

  /**
   * Gets all parking space watches for the user.
   * GET search/parking/watches
   */
  getParkingWatches: () =>
    authClient.get<ParkingWatch[]>(
      `${REAL_ESTATE_V1_API_URL}/search/parking/watches`
    ),

  /**
   * Deletes a parking space watch by token.
   * DELETE search/parking/watch?searchWatchToken=[token]
   */
  deleteParkingWatch: createAsyncTask(
    'delete-parking-watch',
    (searchWatchToken: string) =>
      authClient.delete<void>(
        `${REAL_ESTATE_V1_API_URL}/search/parking/watch`,
        {
          params: { searchWatchToken },
        }
      )
  ),
  /**
   * Terminates a parking contract
   * POST quickrent/parking/terminate
   */
  terminateParkingContract: createAsyncTask(
    'terminate-parking-contract',
    (payload: TerminateParkingContractRequest) =>
      authClient.post<TerminateParkingContractResponse>(
        `${AGREEMENTS_API_BASE_URL}/quickrent/parking/terminate`,
        payload
      )
  ),
};
